/** Everything the door does, in milliseconds: the latch lets go, the slabs stick, then grind aside under their own weight. */
export const DOOR_MS = 3200, DELAY_MS = 500;

type Bezier = readonly [number, number, number, number];

/** CSS-style cubic-bezier easing. */
export function cubicBezier([x1, y1, x2, y2]: Bezier): (t: number) => number {
  const sample = (a: number, c: number, s: number) => ((1 - 3 * c + 3 * a) * s + (3 * c - 6 * a)) * s * s + 3 * a * s;
  const slope = (a: number, c: number, s: number) => 3 * (1 - 3 * c + 3 * a) * s * s + 2 * (3 * c - 6 * a) * s + 3 * a;
  return t => {
    if (t <= 0) return 0; if (t >= 1) return 1;
    let s = t;
    for (let i = 0; i < 6; i++) { const error = sample(x1, x2, s) - t, d = slope(x1, x2, s); if (Math.abs(error) < 1e-6 || Math.abs(d) < 1e-6) break; s -= error / d; }
    let low = 0, high = 1; // Newton wandered out of range: bisect.
    if (s < 0 || s > 1) { s = t; for (let i = 0; i < 24; i++) { if (sample(x1, x2, s) < t) low = s; else high = s; s = (low + high) / 2; } }
    return sample(y1, y2, s);
  };
}

interface Stop { at: number; percent: number; ease: (t: number) => number }
const linear = (t: number) => t;

/**
 * A heavy start: the slabs hold still (and tremble, see `doorState`) while the locks release, then the drag, which labors through
 * one snag before it frees. The slabs never move toward the seam, where they would pass into each other. Percent is of the slab's
 * own width, positive toward the seam.
 */
const STOPS: Stop[] = [
  { at: 0, percent: 0, ease: linear },
  { at: .13, percent: 0, ease: cubicBezier([.7, 0, .9, .4]) },
  { at: .27, percent: -9, ease: cubicBezier([.3, .6, .6, 1]) },
  // The snag only slows the slab: it keeps creeping, then breaks free and gathers speed.
  { at: .46, percent: -15, ease: cubicBezier([.55, 0, .3, 1]) },
  { at: 1, percent: -102, ease: linear },
];

/** How far each slab has travelled at `progress` (0 to 1) of the drag, as a percent of its width; positive is toward the seam. */
export function slabPercent(progress: number): number {
  if (progress <= 0) return 0;
  if (progress >= 1) return STOPS[STOPS.length - 1].percent;
  let i = 0;
  while (STOPS[i + 1].at < progress) i++;
  const from = STOPS[i], to = STOPS[i + 1];
  return from.percent + (to.percent - from.percent) * from.ease((progress - from.at) / (to.at - from.at));
}

const collarEase = cubicBezier([.5, 0, .2, 1]), boltEase = cubicBezier([.6, 0, .2, 1]), rushEase = cubicBezier([.45, 0, .7, 1]);

/** The camera is drawn forward through the doorway while the slabs finish clearing it, so the lintel and sill sweep past overhead. */
export const RUSH_START_MS = DELAY_MS + DOOR_MS * .6, RUSH_MS = 1700;
const ramp = (ms: number, start: number, length: number) => Math.min(1, Math.max(0, (ms - start) / length));

export interface DoorState {
  /** Slab travel, percent of its width, positive toward the seam. */
  slab: number;
  /** The collar around the gem: 0 locked, 1 turned. */
  collar: number;
  /** The three bolts: 0 shot into the left slab, 1 drawn back out of it. */
  bolt: number;
  /** The gem pressed in: 0 at rest, 1 depressed. */
  press: number;
  /** The gem's flare: a bright flash on the touch that settles to a steady glow. */
  flare: number;
  /** The camera's push through the doorway: 0 standing back, 1 through. */
  rush: number;
  /** The slabs' tremble as the locks release, -1 to 1: each rattles up and down against the other, and only ever backs away from the seam. */
  tremble: number;
}

/** The tremble begins just after the touch and dies as the drag takes hold. */
const TREMBLE_START_MS = 120, TREMBLE_END_MS = DELAY_MS + DOOR_MS * .13;
/** A swelling, fading shudder: two incommensurate rattles under a rise-and-fall envelope. */
function tremble(ms: number): number {
  const t = (ms - TREMBLE_START_MS) / (TREMBLE_END_MS - TREMBLE_START_MS);
  if (t <= 0 || t >= 1) return 0;
  const envelope = Math.sin(Math.PI * Math.pow(t, .7)) ** 1.5;
  return envelope * Math.max(-1, Math.min(1, .75 * Math.sin(ms * .21) + .45 * Math.sin(ms * .53 + 1.3)));
}

/** Where every moving part is `ms` after the gem was touched (negative, or never touched, is the sealed door). */
export function doorState(ms: number): DoorState {
  if (!(ms > 0)) return { slab: 0, collar: 0, bolt: 0, press: 0, flare: 0, rush: 0, tremble: 0 };
  const a = Math.min(1, ms / 1400);
  return {
    slab: slabPercent((ms - DELAY_MS) / DOOR_MS),
    collar: collarEase(ramp(ms, 50, 600)),
    bolt: boltEase(ramp(ms, 300, 600)),
    press: ramp(ms, 0, 120),
    flare: a < .22 ? a / .22 : .4 + .6 * (1 - (a - .22) / .78),
    rush: rushEase(ramp(ms, RUSH_START_MS, RUSH_MS)),
    tremble: tremble(ms),
  };
}

/** The moment the slabs have left the screen, and the moment the camera has come through. */
export const DOOR_OPEN_MS = DELAY_MS + DOOR_MS, DOOR_ENTERED_MS = RUSH_START_MS + RUSH_MS;
